import React, { useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Search, Link, Clock, Zap,
  RefreshCw, Eye, MousePointerClick, ExternalLink, X, Trash2
} from 'lucide-react';
import { StatCard } from './StatCard';
import { useDashboardData } from '../hooks/useDashboardData';
import { db } from '../firebaseConfig';
import { doc, deleteDoc } from 'firebase/firestore';

export const Dashboard = () => {
  const { stats, loading, error, userRole } = useDashboardData();
  const [activeTab, setActiveTab] = useState('overview');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [timelineView, setTimelineView] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [deletingEventId, setDeletingEventId] = useState(null);

  const handleDeleteEvent = async (event, e) => {
    e.stopPropagation(); // Prevent event card click
    
    const confirmMessage = `Are you sure you want to delete this event?\n\n` +
      `Type: ${event.event_type}\n` +
      `Query: "${event.query}"\n` +
      `Date: ${new Date(event.timestamp).toLocaleString()}\n\n` +
      `This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      setDeletingEventId(event.docId);
      
      // Delete from Firestore
      const eventDocRef = doc(db, 'users', event.userId, 'events', event.docId);
      await deleteDoc(eventDocRef);
      
      // Refresh the page to show updated data
      window.location.reload();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
      setDeletingEventId(null);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
    window.location.reload();
  };

  const cleanAIResponse = (text) => {
    if (!text) return 'No response available';
    
    return text
      .replace(/An AI Overview is not available.*?Try again later\./g, '')
      .replace(/AI OverviewListenPause.*?Try again later\./g, '')
      .replace(/Error translating content.*?Try again later\./g, '')
      .replace(/\d{1,2}s[A-Za-z\s]+YouTube.*?\d{4}/g, '')
      .replace(/Gemini vs\. ChatGPT:.*?Zapier/g, '')
      .replace(/Google Gemini vs ChatGPT:.*?Authority/g, '')
      .replace(/This video discusses.*?capabilities:/g, '')
      .replace(/You can watch this video.*?ecosystem:/g, '')
      .replace(/\s{3,}/g, '\n\n')
      .trim();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Zap className="mx-auto mb-4 animate-spin text-primary-500" size={48} />
          <p className="text-gray-600 font-medium">Loading research data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-red-600">
          <p className="text-lg font-semibold">Error loading data</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const topicColors = {
    technology: '#3b82f6',    // Bright Blue
    business: '#f59e0b',      // Amber/Orange
    politics: '#ef4444',      // Red
    entertainment: '#ec4899', // Pink
    sports: '#10b981',        // Green
    health: '#06b6d4',        // Cyan
    science: '#8b5cf6',       // Purple
    finance: '#059669',       // Emerald Green
    education: '#f97316',     // Orange
    travel: '#a855f7',        // Violet
    general: '#64748b'        // Gray
  };

  const topicIcons = {
    technology: '💻',
    business: '💼',
    politics: '🏛️',
    entertainment: '🎬',
    sports: '⚽',
    health: '🏥',
    science: '🔬',
    finance: '💰',
    education: '🎓',
    travel: '✈️',
    general: '📌'
  };

  const COLORS = Object.values(topicColors);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex gap-8 items-center">
          <div className="flex gap-4 flex-1">
            {['overview', 'domains', 'topics', 'timeline'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 font-medium transition-all capitalize ${
                  activeTab === tab
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 hover:bg-gray-100 rounded-lg transition-all disabled:opacity-50"
          >
            <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div className="space-y-8 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={Search}
                title="Total Searches"
                value={stats?.totalSearches || 0}
                subtitle="All tracked searches"
                color="blue"
              />
              <StatCard
                icon={Eye}
                title="AI Overview Rate"
                value={`${stats?.aiOverviewRate || 0}%`}
                subtitle="Searches with AI Overview"
                color="green"
              />
              <StatCard
                icon={Link}
                title="Total Citations"
                value={stats?.totalCitations || 0}
                subtitle="Citations extracted"
                color="purple"
              />
              <StatCard
                icon={MousePointerClick}
                title="Citations Clicked"
                value={stats?.citationsClicked || 0}
                subtitle="User interactions"
                color="orange"
              />
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="text-blue-600" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900">Avg. Time to Click</h3>
                  <p className="text-sm text-gray-600">Average time users wait before clicking citations</p>
                </div>
              </div>
              <p className="text-4xl font-bold text-primary-600">{stats?.avgTimeToClick}s</p>
              <p className="text-sm text-gray-500 mt-2">Research Insight: Faster engagement indicates higher citation relevance</p>
            </div>

            {stats?.dailyStats && stats.dailyStats.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📈 Daily Activity Trend</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={stats.dailyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="searches" stroke="#0ea5e9" name="Searches" strokeWidth={2} />
                    <Line type="monotone" dataKey="overviews" stroke="#10b981" name="AI Overviews" strokeWidth={2} />
                    <Line type="monotone" dataKey="clicks" stroke="#f59e0b" name="Citations Clicked" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* DOMAINS TAB */}
        {activeTab === 'domains' && (
          <div className="space-y-8 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🔗 Top Cited Domains</h3>
                {stats?.domains && stats.domains.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.domains}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="domain" angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="citations" fill="#0ea5e9" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-gray-500">No domain data available</p>
                )}
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Domain CTR Analysis</h3>
                {stats?.domains && stats.domains.length > 0 ? (
                  <div className="space-y-3">
                    {stats.domains.slice(0, 8).map((d, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{d.domain}</p>
                          <p className="text-xs text-gray-500">{d.citations} citations • {d.clicks} clicks</p>
                        </div>
                        <div className="text-right">
                          <div className="w-16 h-8 bg-gray-200 rounded relative">
                            <div
                              className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded"
                              style={{ width: `${Math.min(d.ctr, 100)}%` }}
                            />
                          </div>
                          <p className="text-sm font-semibold text-gray-900">{d.ctr}% CTR</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No CTR data available</p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6 overflow-x-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Domain Details</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-semibold text-gray-700">Domain</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Citations</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Clicked</th>
                    <th className="text-center p-3 font-semibold text-gray-700">CTR</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Unique URLs</th>
                    <th className="text-center p-3 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.domains?.map((d, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium text-gray-900">{d.domain}</td>
                      <td className="text-center p-3 text-gray-600">{d.citations}</td>
                      <td className="text-center p-3 text-gray-600">{d.clicks}</td>
                      <td className="text-center p-3">
                        <span className={`px-3 py-1 rounded-full font-semibold ${
                          d.ctr > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {d.ctr}%
                        </span>
                      </td>
                      <td className="text-center p-3">
                        <span className="font-bold text-blue-600">{d.urls?.length || 0}</span>
                      </td>
                      <td className="text-center p-3">
                        <button
                          onClick={() => setSelectedDomain(d)}
                          className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-semibold"
                        >
                          View All URLs
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 mt-4">
                💡 <strong>Tip:</strong> Click "View All URLs" to see every citation URL for that domain.
              </p>
            </div>
          </div>
        )}

        {/* TOPICS TAB */}
        {activeTab === 'topics' && (
          <div className="space-y-8 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🏷️ Query Topic Distribution</h3>
                {stats?.queryTopics && Object.keys(stats.queryTopics).length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={Object.entries(stats.queryTopics).map(([name, data]) => ({
                          name: name.charAt(0).toUpperCase() + name.slice(1),
                          value: data.count,
                          fill: topicColors[name] || '#64748b'
                        }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {Object.keys(stats.queryTopics).map((topic, index) => (
                          <Cell key={`cell-${index}`} fill={topicColors[topic] || COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-gray-500">No topic data available</p>
                )}
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Topic Breakdown</h3>
                <div className="space-y-3">
                  {stats?.queryTopics && Object.entries(stats.queryTopics)
                    .sort((a, b) => b[1].count - a[1].count)
                    .map(([topic, data], idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 rounded"
                      style={{ backgroundColor: `${topicColors[topic]}15` }}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{topicIcons[topic] || '📌'}</span>
                        <div>
                          <p className="font-medium text-gray-900 capitalize">{topic}</p>
                          <p className="text-xs text-gray-500">Search queries</p>
                        </div>
                      </div>
                      <span 
                        className="px-4 py-2 rounded-lg font-bold text-white"
                        style={{ backgroundColor: topicColors[topic] }}
                      >
                        {data.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🔍 Query Category Analysis</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats?.queryCategories && Object.entries(stats.queryCategories).map(([category, data], idx) => (
                  <div key={idx} className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                    <p className="text-sm text-gray-600 capitalize">{category}</p>
                    <p className="text-2xl font-bold text-blue-800">{data.count}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* TIMELINE TAB with 3 Views */}
        {activeTab === 'timeline' && (
          <div className="space-y-8 animate-fade-in">
            {/* View Selector Tabs */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => setTimelineView('all')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    timelineView === 'all'
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  📋 All Events
                </button>
                {userRole === 'admin' && (
                  <button
                    onClick={() => setTimelineView('byUser')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      timelineView === 'byUser'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    👤 By User
                  </button>
                )}
                <button
                  onClick={() => setTimelineView('byTopic')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    timelineView === 'byTopic'
                      ? 'bg-primary-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🏷️ By Topic
                </button>
              </div>
            </div>

            {/* ALL EVENTS VIEW */}
            {timelineView === 'all' && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">📅 All Events Timeline</h3>
                <div className="space-y-4">
                  {stats?.recentEvents && stats.recentEvents.length > 0 ? (
                    stats.recentEvents.map((event, idx) => (
                      <div 
                        key={idx} 
                        onClick={() => {
                          if (event.event_type === 'ai_overview_shown') {
                            setSelectedEvent(event);
                          } else if (event.event_type === 'citation_clicked' && event.citation_url) {
                            window.open(event.citation_url, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        className={`p-4 border-l-4 bg-gray-50 rounded transition-all ${
                          event.event_type === 'ai_overview_shown' ? 'border-primary-500 cursor-pointer hover:bg-blue-50 hover:shadow-md' : 
                          event.event_type === 'citation_clicked' ? 'border-green-500 cursor-pointer hover:bg-green-50 hover:shadow-md' : 
                          'border-gray-400'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <p className="font-semibold text-gray-900">
                                {event.event_type === 'ai_overview_shown' ? '🤖 AI Overview' :
                                 event.event_type === 'citation_clicked' ? '🖱️ Citation Clicked' :
                                 '🔍 Search (No Overview)'}
                              </p>
                              {event.query_topic && (
                                <span 
                                  className="px-2 py-1 rounded text-xs font-semibold text-white"
                                  style={{ backgroundColor: topicColors[event.query_topic] }}
                                >
                                  {topicIcons[event.query_topic]} {event.query_topic}
                                </span>
                              )}
                              {event.event_type === 'citation_clicked' && (
                                <ExternalLink size={14} className="text-green-600" />
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              Query: <span className="font-medium">"{event.query}"</span>
                            </p>
                            {event.event_type === 'citation_clicked' && (
                              <p className="text-sm text-green-600 mt-2">
                                Clicked: <span className="underline font-semibold">
                                  {event.citation_domain}
                                </span> (Position {event.citation_position}) • Click to visit URL
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {new Date(event.timestamp).toLocaleString()}
                            </span>
                            <button
                              onClick={(e) => handleDeleteEvent(event, e)}
                              disabled={deletingEventId === event.docId}
                              className={`p-2 rounded-lg transition-all ${
                                deletingEventId === event.docId
                                  ? 'bg-gray-200 cursor-not-allowed'
                                  : 'bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-700'
                              }`}
                              title="Delete this event"
                            >
                              {deletingEventId === event.docId ? (
                                <div className="animate-spin h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full"></div>
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </div>
                        </div>
                        {event.cited_sources && event.cited_sources.length > 0 && (
                          <div className="mt-3 text-sm">
                            <p className="text-gray-700 mb-2">Citations: {event.cited_sources.length}</p>
                            <div className="flex flex-wrap gap-2">
                              {event.cited_sources.slice(0, 3).map((source, sidx) => (
                                <span key={sidx} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                  {source.domain}
                                </span>
                              ))}
                              {event.cited_sources.length > 3 && (
                                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                                  +{event.cited_sources.length - 3} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {event.event_type === 'ai_overview_shown' && (
                          <p className="text-xs text-blue-600 mt-2">💡 Click to view full details →</p>
                        )}
                        {event.event_type === 'citation_clicked' && (
                          <p className="text-xs text-green-600 mt-2">🔗 Click to visit this citation URL →</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-8">No recent events</p>
                  )}
                </div>
              </div>
            )}

            {/* BY USER VIEW */}
            {timelineView === 'byUser' && (
              <div className="space-y-6">
                {stats?.eventsByUser && Object.keys(stats.eventsByUser).length > 0 ? (
                  <>
                    {/* User Selector Dropdown */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex-1 min-w-[250px]">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            👤 Select User to View
                          </label>
                          <select
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900"
                          >
                            <option value="">-- Select a user --</option>
                            {Object.entries(stats.eventsByUser)
                              .sort((a, b) => b[1].length - a[1].length)
                              .map(([userId, userEvents]) => (
                                <option key={userId} value={userId}>
                                  {userId} ({userEvents.length} events)
                                </option>
                              ))}
                          </select>
                        </div>
                        <div className="flex gap-2">
                          <div className="px-4 py-2 bg-blue-50 rounded-lg">
                            <p className="text-xs text-gray-600">Total Users</p>
                            <p className="text-2xl font-bold text-blue-800">
                              {Object.keys(stats.eventsByUser).length}
                            </p>
                          </div>
                          <div className="px-4 py-2 bg-green-50 rounded-lg">
                            <p className="text-xs text-gray-600">Total Events</p>
                            <p className="text-2xl font-bold text-green-800">
                              {Object.values(stats.eventsByUser).reduce((sum, events) => sum + events.length, 0)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Selected User Events */}
                    {selectedUserId && stats.eventsByUser[selectedUserId] ? (
                      <div className="bg-white rounded-lg border-2 border-primary-500 p-6 shadow-lg">
                        <div className="flex items-center justify-between mb-6 pb-4 border-b">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-white text-xl font-bold">
                                {selectedUserId.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-gray-900">{selectedUserId}</h3>
                                <p className="text-sm text-gray-600">
                                  {stats.eventsByUser[selectedUserId].length} total events
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500 mb-1">First Activity</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {new Date(stats.eventsByUser[selectedUserId][stats.eventsByUser[selectedUserId].length - 1]?.timestamp).toLocaleString()}
                            </p>
                            <p className="text-sm text-gray-500 mt-2 mb-1">Latest Activity</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {new Date(stats.eventsByUser[selectedUserId][0]?.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>

                        {/* User Statistics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-xs text-gray-600">AI Overviews</p>
                            <p className="text-xl font-bold text-blue-800">
                              {stats.eventsByUser[selectedUserId].filter(e => e.event_type === 'ai_overview_shown').length}
                            </p>
                          </div>
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-xs text-gray-600">Citations Clicked</p>
                            <p className="text-xl font-bold text-green-800">
                              {stats.eventsByUser[selectedUserId].filter(e => e.event_type === 'citation_clicked').length}
                            </p>
                          </div>
                          <div className="p-3 bg-purple-50 rounded-lg">
                            <p className="text-xs text-gray-600">Searches</p>
                            <p className="text-xl font-bold text-purple-800">
                              {stats.eventsByUser[selectedUserId].filter(e => e.event_type === 'search_without_ai_overview').length}
                            </p>
                          </div>
                          <div className="p-3 bg-orange-50 rounded-lg">
                            <p className="text-xs text-gray-600">Top Topic</p>
                            <p className="text-xl font-bold text-orange-800 capitalize">
                              {(() => {
                                const topics = {};
                                stats.eventsByUser[selectedUserId].forEach(e => {
                                  const topic = e.query_topic || 'general';
                                  topics[topic] = (topics[topic] || 0) + 1;
                                });
                                const topTopic = Object.entries(topics).sort((a, b) => b[1] - a[1])[0];
                                return topTopic ? `${topicIcons[topTopic[0]]} ${topTopic[0]}` : 'N/A';
                              })()}
                            </p>
                          </div>
                        </div>

                        {/* Event Timeline */}
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 mb-4">📅 Activity Timeline</h4>
                          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                            {stats.eventsByUser[selectedUserId].map((event, idx) => (
                              <div 
                                key={idx}
                                onClick={() => {
                                  if (event.event_type === 'ai_overview_shown') {
                                    setSelectedEvent(event);
                                  } else if (event.event_type === 'citation_clicked' && event.citation_url) {
                                    window.open(event.citation_url, '_blank', 'noopener,noreferrer');
                                  }
                                }}
                                className={`p-4 border-l-4 bg-gray-50 rounded transition-all ${
                                  event.event_type === 'ai_overview_shown' ? 'border-primary-500 cursor-pointer hover:bg-blue-50 hover:shadow-md' : 
                                  event.event_type === 'citation_clicked' ? 'border-green-500 cursor-pointer hover:bg-green-50 hover:shadow-md' : 
                                  'border-gray-400 hover:bg-gray-100'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                      <span className="px-2 py-1 bg-white rounded text-xs font-bold text-gray-500">
                                        #{stats.eventsByUser[selectedUserId].length - idx}
                                      </span>
                                      <p className="font-semibold text-gray-900">
                                        {event.event_type === 'ai_overview_shown' ? '🤖 AI Overview' :
                                         event.event_type === 'citation_clicked' ? '🖱️ Citation Click' :
                                         '🔍 Search'}
                                      </p>
                                      {event.query_topic && (
                                        <span 
                                          className="px-2 py-1 rounded text-xs font-semibold text-white"
                                          style={{ backgroundColor: topicColors[event.query_topic] }}
                                        >
                                          {topicIcons[event.query_topic]} {event.query_topic}
                                        </span>
                                      )}
                                      {event.event_type === 'citation_clicked' && (
                                        <ExternalLink size={14} className="text-green-600" />
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-700 font-medium mt-1">
                                      Query: <span className="text-gray-900">"{event.query}"</span>
                                    </p>
                                    {event.citation_count > 0 && (
                                      <p className="text-xs text-gray-500 mt-2">
                                        📎 {event.citation_count} citations
                                      </p>
                                    )}
                                    {event.event_type === 'citation_clicked' && event.citation_domain && (
                                      <p className="text-xs text-green-600 mt-2">
                                        🔗 Clicked: <span className="font-semibold">{event.citation_domain}</span> • Click to visit
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-right ml-4">
                                    <span className="text-xs text-gray-500 block">
                                      {new Date(event.timestamp).toLocaleDateString()}
                                    </span>
                                    <span className="text-sm font-semibold text-gray-900 block mt-1">
                                      {new Date(event.timestamp).toLocaleTimeString()}
                                    </span>
                                  </div>
                                </div>
                                {event.event_type === 'ai_overview_shown' && (
                                  <p className="text-xs text-blue-600 mt-3">💡 Click to view full AI Overview details</p>
                                )}
                                {event.event_type === 'citation_clicked' && (
                                  <p className="text-xs text-green-600 mt-3">🔗 Click to visit this citation URL</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white rounded-lg border border-gray-200 p-12">
                        <div className="text-center">
                          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-4xl">👤</span>
                          </div>
                          <p className="text-gray-600 text-lg font-medium">Select a user from the dropdown above</p>
                          <p className="text-gray-500 text-sm mt-2">Choose a user to view their complete activity timeline</p>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <p className="text-gray-500 text-center py-8">No user data available</p>
                  </div>
                )}
              </div>
            )}

            {/* BY TOPIC VIEW */}
            {timelineView === 'byTopic' && (
              <div className="space-y-6">
                {stats?.eventsByTopic && Object.entries(stats.eventsByTopic).length > 0 ? (
                  Object.entries(stats.eventsByTopic)
                    .sort((a, b) => b[1].length - a[1].length)
                    .map(([topic, topicEvents]) => (
                    <div 
                      key={topic} 
                      className="bg-white rounded-lg border border-gray-200 p-6"
                      style={{ borderLeftWidth: '4px', borderLeftColor: topicColors[topic] || '#64748b' }}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{topicIcons[topic] || '📌'}</span>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 capitalize">{topic}</h3>
                            <p className="text-sm text-gray-600">{topicEvents.length} events</p>
                          </div>
                        </div>
                        <div 
                          className="px-4 py-2 rounded-lg text-white font-bold"
                          style={{ backgroundColor: topicColors[topic] || '#64748b' }}
                        >
                          {topicEvents.length}
                        </div>
                      </div>
                      <div className="space-y-3">
                        {topicEvents.slice(0, 10).map((event, idx) => (
                          <div 
                            key={idx}
                            onClick={() => {
                              if (event.event_type === 'ai_overview_shown') {
                                setSelectedEvent(event);
                              } else if (event.event_type === 'citation_clicked' && event.citation_url) {
                                window.open(event.citation_url, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            className={`p-3 bg-gray-50 rounded text-sm transition-all ${
                              event.event_type === 'ai_overview_shown' ? 'cursor-pointer hover:bg-blue-50 hover:shadow-md' : 
                              event.event_type === 'citation_clicked' ? 'cursor-pointer hover:bg-green-50 hover:shadow-md' : 
                              'hover:bg-gray-100'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-gray-900">
                                    {event.event_type === 'ai_overview_shown' ? '🤖' :
                                     event.event_type === 'citation_clicked' ? '🖱️' : '🔍'}{' '}
                                    "{event.query}"
                                  </p>
                                  {event.event_type === 'citation_clicked' && (
                                    <ExternalLink size={14} className="text-green-600" />
                                  )}
                                </div>
                                {event.citation_count > 0 && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {event.citation_count} citations
                                  </p>
                                )}
                                {event.event_type === 'citation_clicked' && event.citation_domain && (
                                  <p className="text-xs text-green-600 mt-1">
                                    🔗 <span className="font-semibold">{event.citation_domain}</span> • Click to visit
                                  </p>
                                )}
                                {event.event_type === 'ai_overview_shown' && (
                                  <p className="text-xs text-blue-600 mt-1">
                                    💡 Click to view AI Overview details
                                  </p>
                                )}
                              </div>
                              <span className="text-xs text-gray-500 ml-4">
                                {new Date(event.timestamp).toLocaleTimeString()}
                              </span>
                            </div>
                          </div>
                        ))}
                        {topicEvents.length > 10 && (
                          <p className="text-xs text-gray-500 text-center pt-2">
                            +{topicEvents.length - 10} more events
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <p className="text-gray-500 text-center py-8">No topic data available</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-gray-200 text-center text-gray-600 text-sm">
          <p>AI Overview Tracker • Research Analytics Platform</p>
          <p className="mt-2">Data last synced: {new Date().toLocaleString()}</p>
        </div>
      </div>

      {/* DOMAIN DETAIL MODAL */}
      {selectedDomain && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Domain: {selectedDomain.domain}</h2>
                <p className="text-gray-600 mt-1">
                  {selectedDomain.citations} citations • {selectedDomain.clicks} clicks • {selectedDomain.ctr}% CTR
                </p>
              </div>
              <button 
                onClick={() => setSelectedDomain(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                🔗 All Citation URLs ({selectedDomain.urls?.length || 0})
              </h3>
              <div className="space-y-2">
                {selectedDomain.urls && selectedDomain.urls.map((url, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate font-medium">
                        URL #{idx + 1}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{url}</p>
                    </div>
                    <a 
                      href={url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="ml-4 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-semibold flex items-center gap-2 flex-shrink-0"
                    >
                      <ExternalLink size={14} />
                      Open
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EVENT DETAIL MODAL */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between z-10">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">Query Details</h2>
                <p className="text-gray-600 mt-1">"{selectedEvent.query}"</p>
              </div>
              <button 
                onClick={() => setSelectedEvent(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div 
                  className="p-4 rounded-lg text-white"
                  style={{ backgroundColor: topicColors[selectedEvent.query_topic] || '#64748b' }}
                >
                  <p className="text-sm opacity-90">Topic</p>
                  <p className="text-lg font-bold capitalize">
                    {topicIcons[selectedEvent.query_topic]} {selectedEvent.query_topic}
                  </p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">Citations</p>
                  <p className="text-lg font-bold text-green-800">{selectedEvent.citation_count}</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-gray-600">Category</p>
                  <p className="text-lg font-bold text-purple-800 capitalize">{selectedEvent.query_category}</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-gray-600">Time</p>
                  <p className="text-lg font-bold text-orange-800">
                    {new Date(selectedEvent.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📎 Citations ({selectedEvent.cited_sources?.length || 0})</h3>
                <div className="space-y-2">
                  {selectedEvent.cited_sources && selectedEvent.cited_sources.map((source, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-sm font-semibold text-gray-500 flex-shrink-0">#{source.position}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900">{source.domain}</p>
                          <p className="text-xs text-gray-500 truncate">{source.text || 'No title'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {source.clicked && (
                          <span className="text-green-600 text-sm font-semibold whitespace-nowrap">✅ Clicked</span>
                        )}
                        <a 
                          href={source.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-blue-100 rounded-lg text-blue-600 flex-shrink-0"
                          title="Open URL"
                        >
                          <ExternalLink size={16} />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🤖 AI Overview Response</h3>
                <div className="p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg max-h-96 overflow-y-auto">
                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                    {cleanAIResponse(selectedEvent.ai_response_text)}
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Response text has been cleaned for readability
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
